import { onUnmounted, ref, watch } from "vue";

import { useWorktreeStore } from "@/composables/worktree-manager";
import { useCurrentEntityStore } from "@/stores/entity-store";

// Entering or leaving a draft flips the read revision, and every store that
// watches it refetches on its own. Nothing aggregates those, so the switch is
// treated as over once the two that redraw the visible page — the tree and the
// current entity — are both idle again.
//
// The grace window covers switches that start no fetch at all (a page with no
// entity, a tree already at that revision); the cap makes sure a stalled
// request can never strand the overlay over the app.
const GRACE_MS = 400;
const CAP_MS = 6000;

export const isSwitchingDraft = ref(false);
export const draftSwitchLabel = ref("");

// Bumped on every beginDraftSwitch call, including one that starts while
// isSwitchingDraft is already true (switching straight from one draft to
// another) — a plain `watch(isSwitchingDraft, ...)` would miss that case,
// since Vue doesn't refire a watcher when a ref is set to its current value,
// leaving the first switch's sawFetch/timers stale for the second one.
const switchToken = ref(0);

/**
 * Raise the overlay. Call once the revision has actually flipped — the refetch
 * watchers key off that, so starting the clock any earlier can expire the grace
 * window before the first request is even made.
 */
export const beginDraftSwitch = (label: string) => {
    draftSwitchLabel.value = label;
    isSwitchingDraft.value = true;
    switchToken.value++;
};

export const endDraftSwitch = () => {
    isSwitchingDraft.value = false;
};

/**
 * Lowers the overlay again. Lives in the layout rather than beside
 * `beginDraftSwitch` so that entering a draft stays free of any dependency on
 * the stores being watched here.
 */
export const useDraftSwitchTracking = () => {
    const worktree = useWorktreeStore();
    const entity = useCurrentEntityStore();

    let sawFetch = false;
    let graceTimer: ReturnType<typeof setTimeout> | undefined;
    let capTimer: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
        clearTimeout(graceTimer);
        clearTimeout(capTimer);
    };

    const isFetching = () => worktree.isFetching || entity.isFetching;

    // Fires on every beginDraftSwitch (see switchToken above), so a switch
    // started mid-switch restarts this tracking instead of running out the
    // previous switch's clock.
    watch(switchToken, () => {
        clearTimers();

        sawFetch = isFetching();
        if (!sawFetch) {
            graceTimer = setTimeout(() => {
                if (!sawFetch) endDraftSwitch();
            }, GRACE_MS);
        }
        capTimer = setTimeout(endDraftSwitch, CAP_MS);
    });

    watch(isFetching, (busy) => {
        if (!isSwitchingDraft.value) return;
        if (busy) {
            sawFetch = true;
            clearTimeout(graceTimer);
        } else if (sawFetch) {
            endDraftSwitch();
        }
    });

    onUnmounted(() => {
        clearTimers();
        endDraftSwitch();
    });
};
